import type { Application } from '@hotwired/stimulus';
import { Controller } from '@hotwired/stimulus';
import { bindDataToTemplate, humanFileSize } from '../../500-common/util';

/**
 * Progressive enhances input fields of type 'file' with the list of attached files
 * and the possibility to individually remove files before proceeding.
 * Requires an additional HTMLTemplate for the items of the list, see file-input.template.twig
 * @example <input type="file" multiple data-controller="file-input-field" />
 */
export class FileInputFieldController extends Controller<HTMLElement> {
    static targets = [
        'input',
        'filelist',
        'filelistItemTemplate',
        'filelistItem',
        'counterTemplate',
        'counter',
        'label',
    ];

    static values = {
        max: {
            type: Number,
            default: -1,
        },
    };

    declare readonly hasInputTarget: boolean;
    declare readonly inputTarget: HTMLInputElement;

    declare readonly hasFilelistTarget: boolean;
    declare readonly filelistTarget: HTMLUListElement;

    declare readonly hasFilelistItemTemplateTarget: boolean;
    declare readonly filelistItemTemplateTarget: HTMLTemplateElement;

    declare readonly hasFilelistItemTarget: HTMLLIElement;
    declare readonly filelistItemTargets: HTMLLIElement[];

    declare readonly hasCounterTemplateTarget: HTMLElement;
    declare readonly counterTemplateTarget: HTMLTemplateElement;
    declare readonly hasCounterTarget: HTMLElement;
    declare readonly counterTarget: HTMLElement;

    declare readonly labelTarget: HTMLLabelElement;

    declare readonly maxValue: number;

    static register(application: Application) {
        application.register('file-input-field', this);
    }

    /**
     * Get the current selected files
     */
    get files(): FileList | unknown[] {
        if (this.hasInputTarget) {
            return this.inputTarget.files;
        }

        return [];
    }

    connect() {
        try {
            this.initFilelist();
            this.element.classList.add('js');
        } catch (e) {
            console.error(`'Could not progressive enhance file input field`, e);
        }
    }

    initFilelist() {
        const listElement = document.createElement('UL');
        listElement.setAttribute('data-file-input-field-target', 'filelist');
        listElement.setAttribute('role', 'list'); // Safari Accessibility Fix
        listElement.hidden = true;

        this.element.append(listElement);
        this.updateCounter();
    }

    /**
     * [data-action="file-input-field#onFileAdd"]
     */
    onFileAdd(e: Event) {
        if (this.maxValue === -1 || this.files.length <= this.maxValue) {
            this.updateFilelist();
        } else {
            // Remove all files that are over the maximum defined
            for (let index = 1; this.files.length > this.maxValue; index++) {
                this.removeFile(this.files.length - index);
            }
        }
    }

    /**
     * [data-action="file-input-field#onFileRemove"]
     */
    onFileRemove(e: Partial<Event>) {
        const selectedFilelistItemElement = (e.currentTarget as Node).parentNode;

        const fileIndex = this.filelistItemTargets.findIndex((target) => target === selectedFilelistItemElement);
        this.removeFile(fileIndex);
    }

    private removeFile(fileIndex: number) {
        const fileBuffer = Array.from(this.files as FileList);
        fileBuffer.splice(fileIndex, 1);

        // Files cannot be directly removed from the file list.
        // But with the DataTransfer API we can replace the file list.
        const dT = new DataTransfer();
        for (const file of fileBuffer) {
            dT.items.add(file);
        }

        this.inputTarget.files = dT.files;

        this.updateFilelist();
    }

    private updateFilelist() {
        if (this.files.length < 1) {
            this.hideFilelist();
        } else {
            this.showFilelist();
        }

        this.filelistTarget.innerHTML = '';

        for (const file of this.files as FileList) {
            const itemTemplate = this.filelistItemTemplateTarget.content.firstElementChild.cloneNode(
                true
            ) as HTMLElement;

            const fileData = {
                name: file.name,
                size: humanFileSize(file.size),
            };

            const itemHTML = bindDataToTemplate(fileData, itemTemplate);
            this.filelistTarget.insertAdjacentHTML('beforeend', itemHTML);
        }

        this.updateCounter();
    }

    private updateCounter() {
        if (this.hasCounterTarget) {
            this.counterTarget.remove();
        }

        if (this.hasCounterTemplateTarget && this.maxValue !== -1) {
            const counterTemplate = this.counterTemplateTarget.content.firstElementChild.cloneNode(true) as HTMLElement;
            const counterHTML = bindDataToTemplate(
                {
                    counter: this.files.length,
                    max: this.maxValue,
                },
                counterTemplate
            );

            this.labelTarget.insertAdjacentHTML('beforeend', counterHTML);
        }
    }

    private hideFilelist() {
        this.filelistTarget.hidden = true;
    }

    private showFilelist() {
        this.filelistTarget.hidden = false;
    }
}

export default FileInputFieldController;
